import React, { useState, useEffect, useMemo } from 'react';
import { carsAPI } from '../services/api';
import { useCart } from '../context/CartContext';
import WhatsAppButton from './WhatsAppButton';
import './SparePartsGrid.css';

/** قائمة عناوين الصور: الرئيسية ثم الإضافية */
const getAllImageUrls = (item) => {
  const main = item.image_url || null;
  const extra = item.extra_image_urls || [];
  return [main, ...extra].filter(Boolean);
};

/** معرض صورة واحدة أو كاروسيل للعديد من الصور */
const PartCardImage = ({ item }) => {
  const urls = getAllImageUrls(item);
  const [index, setIndex] = useState(0);
  const currentUrl = urls[index] || null;
  const hasMultiple = urls.length > 1;

  if (urls.length === 0) {
    return (
      <div className="spare-part-card-image-wrap">
        <div className="spare-part-image-placeholder" style={{ display: 'flex' }}>
          <i className="fas fa-cog" aria-hidden="true" />
          <span>لا توجد صورة</span>
        </div>
      </div>
    );
  }

  return (
    <div className="spare-part-card-image-wrap spare-part-card-image-wrap--gallery">
      <img
        src={currentUrl}
        alt={item.name_ar || item.name}
        className="spare-part-image"
        onError={(e) => {
          e.target.style.display = 'none';
          const next = e.target.nextElementSibling;
          if (next) next.style.display = 'flex';
        }}
      />
      <div className="spare-part-image-placeholder" style={{ display: 'none' }}>
        <i className="fas fa-cog" aria-hidden="true" />
        <span>لا توجد صورة</span>
      </div>
      {hasMultiple && (
        <>
          <button
            type="button"
            className="spare-part-gallery-btn spare-part-gallery-btn--prev"
            onClick={(e) => {
              e.stopPropagation();
              setIndex((i) => (i === 0 ? urls.length - 1 : i - 1));
            }}
            aria-label="الصورة السابقة"
          >
            <i className="fas fa-chevron-right" />
          </button>
          <button
            type="button"
            className="spare-part-gallery-btn spare-part-gallery-btn--next"
            onClick={(e) => {
              e.stopPropagation();
              setIndex((i) => (i + 1) % urls.length);
            }}
            aria-label="الصورة التالية"
          >
            <i className="fas fa-chevron-left" />
          </button>
          <div className="spare-part-gallery-dots" aria-hidden="true">
            {urls.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`spare-part-gallery-dot ${i === index ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setIndex(i);
                }}
                aria-label={`صورة ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const SparePartsGrid = ({ area, variantKey, variant, category }) => {
  const { addToCart } = useCart();
  const [spareParts, setSpareParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const vehicleContext = useMemo(() => {
    if (!variant) return {};
    const m = variant.model;
    return {
      modelName: (m && m.name) || variant.model_name || '',
      variantName: variant.name || '',
      modelSlug: (m && m.slug) || variant.model_slug || '',
      variantSlug: variant.slug || '',
    };
  }, [variant]);

  useEffect(() => {
    if (area && variantKey) {
      fetchSpareParts();
    } else {
      setSpareParts([]);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area, variantKey, category]);

  const fetchSpareParts = async () => {
    try {
      setLoading(true);
      const isAccessories = category === 'accessories';
      const validArea = ['front', 'rear', 'side', 'interior'].includes(area) ? area : null;
      const response = isAccessories
        ? await carsAPI.getVariantAccessories(variantKey, validArea)
        : await carsAPI.getVariantSpareParts(variantKey, validArea);
      const data = response.data.results || response.data;
      const list = Array.isArray(data) ? data : [];
      const filtered =
        validArea && list.length
          ? list.filter((p) => (p.area || '').toLowerCase() === validArea)
          : list;
      setSpareParts(filtered);
      setError(null);
    } catch (err) {
      setError(category === 'accessories' ? 'فشل في تحميل الكماليات' : 'فشل في تحميل قطع الغيار');
      console.error('Error fetching spare parts:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="spare-parts-grid">
      <div className="spare-parts-list">
        {spareParts.map((sparePart) => (
          <div key={sparePart.id} className="spare-part-card card">
            <PartCardImage item={sparePart} />
            <div className="spare-part-info">
              <h3 className="spare-part-name">{sparePart.name_ar || sparePart.name}</h3>
              {sparePart.part_number != null && sparePart.part_number !== '' && (
                <p className="spare-part-number">
                  رقم القطعة:
                  <strong>{sparePart.part_number}</strong>
                </p>
              )}
              {sparePart.price && (
                <p className="spare-part-price">
                  السعر: <strong>{sparePart.price} د.ل</strong>
                </p>
              )}
              <div className="spare-part-status">
                <span
                  className={`status-badge ${sparePart.in_stock ? 'in-stock' : 'out-of-stock'}`}
                >
                  {sparePart.in_stock ? 'متوفر' : 'غير متوفر'}
                </span>
              </div>
              {sparePart.description && (
                <p className="spare-part-description">{sparePart.description}</p>
              )}
              <div className="spare-part-card-actions">
                <button
                  type="button"
                  className="spare-part-add-cart-btn"
                  onClick={() => addToCart(sparePart, 1, vehicleContext)}
                >
                  <i className="fas fa-cart-plus" /> أضف القطعة للسلة
                </button>
                <WhatsAppButton sparePart={sparePart} vehicleContext={vehicleContext} />
              </div>
            </div>
          </div>
        ))}
      </div>
      {spareParts.length === 0 && (
        <div className="no-spare-parts">
          {category === 'accessories' ? 'لا توجد كماليات متاحة' : 'لا توجد قطع غيار متاحة'}
        </div>
      )}
    </div>
  );
};

export default SparePartsGrid;
